import React from "react";
import {AlertTriangle} from "lucide-react";
import {REST_HATCH, restHoursLabel, restKindLabel} from "../../calander/restDays";
import {formatDuration} from "./attendanceApi";
import {workDayMinutes, workSegments} from "./attendanceStatus";

// The status colours are always paired with a letter or a label, so that they
// stay readable without seeing the colour.
export const STATUS_META = {
    present: {label: "Présent", cell: "bg-primary/15 text-teal-800 dark:text-teal-300", pill: "bg-primary/15 text-teal-800 dark:text-teal-300"},
    open: {label: "En cours", cell: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", pill: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"},
    missing_out: {label: "Sortie manquante", cell: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300", pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"},
    absent: {label: "Absent", letter: "A", cell: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", pill: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"},
    pending: {label: "Pas encore pointé", letter: "?", cell: "border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400", pill: "border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"},
    rest: {label: "Repos", letter: "R", cell: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300", pill: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300", hatch: true},
    leave: {label: "Congé", letter: "C", cell: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300", pill: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300"},
    closed: {label: "Fermé", letter: "F", cell: "text-gray-400 dark:text-gray-500", pill: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400", hatch: true},
};

const LEGEND = ["present", "open", "missing_out", "absent", "rest", "leave", "closed"];

export const StatusPill = ({status}) => {
    const meta = STATUS_META[status];
    if (!meta) return null;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.pill}`}>
            {meta.label}
        </span>
    );
};

export const Legend = () => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 sm:px-6 py-3 text-xs text-gray-600 dark:text-gray-300">
        {LEGEND.map((key) => {
            const meta = STATUS_META[key];
            return (
                <span key={key} className="inline-flex items-center gap-1.5">
                    <span style={meta.hatch ? REST_HATCH : undefined}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${meta.cell}`}>
                        {meta.letter || ""}
                    </span>
                    {meta.label}
                </span>
            );
        })}
    </div>
);

export const KpiStrip = ({items}) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-b border-gray-200 dark:border-gray-700 divide-x divide-y lg:divide-y-0 divide-gray-200 dark:divide-gray-700">
        {items.map((kpi) => (
            <div key={kpi.label} className="px-4 sm:px-6 py-3">
                <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate" title={kpi.label}>{kpi.label}</div>
                <div className={`text-xl font-bold tabular-nums ${kpi.alert ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"}`}>{kpi.value}</div>
            </div>
        ))}
    </div>
);

// "7h30" is too wide for a grid cell: "7:30"
const compactDuration = (minutes) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;

const cellText = ({status, day}) => {
    if (status === "present") return compactDuration(day.worked_minutes);
    if (status === "open") return "•••";
    if (status === "missing_out") return "!";
    return STATUS_META[status]?.letter || "";
};

export const cellTitle = ({status, day, rests, workedOnRest}, dateLabel) => {
    const parts = [dateLabel, STATUS_META[status]?.label];
    if (day) {
        parts.push(`${day.first_in || "–"} → ${day.last_out || "–"}`, formatDuration(day.worked_minutes));
    }
    if (workedOnRest) parts.push("A travaillé pendant son repos");
    rests.forEach((rest) => parts.push(`${restKindLabel(rest)} : ${restHoursLabel(rest)}`));
    return parts.filter(Boolean).join(" · ");
};

export const DayCell = ({cell, label, onClick, className = "", children}) => {
    const meta = STATUS_META[cell.status];
    const title = cellTitle(cell, label);
    if (!meta) {
        return <div aria-hidden="true" className={`rounded bg-gray-50 dark:bg-gray-800/60 ${className}`}/>;
    }
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={title}
            style={meta.hatch ? REST_HATCH : undefined}
            className={`relative flex items-center justify-center rounded text-[11px] font-semibold tabular-nums hover:ring-2 hover:ring-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow ${meta.cell} ${className}`}
        >
            {children || cellText(cell)}
            {cell.workedOnRest && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500"/>}
        </button>
    );
};

const headerLabel = (date) => {
    const asDate = new Date(`${date}T12:00:00`);
    return {
        weekday: asDate.toLocaleDateString("fr-FR", {weekday: "short"}).replace(".", ""),
        day: asDate.getDate(),
        long: asDate.toLocaleDateString("fr-FR", {weekday: "long", day: "numeric", month: "long"}),
        weekend: asDate.getDay() === 0 || asDate.getDay() === 6,
    };
};

// ─── Period: employees × days ────────────────────────────────────────────────

export const PresenceGrid = ({rows, today, onName, onCell}) => {
    const dates = rows[0]?.cells.map((c) => c.date) || [];
    const stickyName = "sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700";
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead>
                <tr>
                    <th scope="col" className={`${stickyName} px-4 sm:px-6 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-b`}>
                        Employé
                    </th>
                    {dates.map((date) => {
                        const label = headerLabel(date);
                        return (
                            <th key={date} scope="col" title={label.long}
                                className={`px-0.5 py-2 text-center font-medium border-b border-gray-200 dark:border-gray-700 ${date === today ? "text-primary" : label.weekend ? "text-gray-400" : "text-gray-600 dark:text-gray-300"}`}>
                                <div className="text-[10px] uppercase">{label.weekday}</div>
                                <div className="text-xs tabular-nums">{label.day}</div>
                            </th>
                        );
                    })}
                    <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-b border-l border-gray-200 dark:border-gray-700">Heures</th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Abs.</th>
                </tr>
                </thead>
                <tbody>
                {rows.map((row) => (
                    <tr key={row.key}>
                        <th scope="row" className={`${stickyName} px-4 sm:px-6 py-1 text-left font-semibold border-b max-w-[11rem] sm:max-w-[14rem]`}>
                            <button type="button" onClick={() => onName(row)} title={row.name}
                                    className={`block w-full truncate text-left hover:underline ${row.unknown ? "text-amber-600" : "text-gray-900 dark:text-gray-100"}`}>
                                {row.name}
                            </button>
                        </th>
                        {row.cells.map((cell) => (
                            <td key={cell.date} className="p-0.5 border-b border-gray-100 dark:border-gray-700/60">
                                <DayCell cell={cell} label={headerLabel(cell.date).long} onClick={() => onCell(row, cell)}
                                         className="w-10 h-8 min-w-[2.5rem] mx-auto"/>
                            </td>
                        ))}
                        <td className="px-3 py-1 text-right font-semibold tabular-nums whitespace-nowrap text-gray-900 dark:text-gray-100 border-b border-l border-gray-200 dark:border-gray-700">
                            {formatDuration(row.minutes)}
                        </td>
                        <td className={`px-3 py-1 text-right tabular-nums border-b border-gray-200 dark:border-gray-700 ${row.absences ? "font-semibold text-red-600 dark:text-red-400" : "text-gray-400"}`}>
                            {row.unknown ? "—" : row.absences}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Single day: when each employee was there ────────────────────────────────

const clock = (minutes) => `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}h`;

const hourTicks = ({start, end}) => {
    const step = end - start > 12 * 60 ? 120 : 60;
    const ticks = [];
    for (let minutes = Math.ceil(start / 60) * 60; minutes <= end; minutes += step) ticks.push(minutes);
    return ticks;
};

const punchSummary = (day) => {
    const kept = day.punches.filter((p) => !p.duplicate).map((p) => p.time);
    const pairs = [];
    for (let i = 0; i < kept.length; i += 2) pairs.push(`${kept[i]} → ${kept[i + 1] || "…"}`);
    return pairs.join("  ·  ");
};

export const DayTimeline = ({rows, window: range, dayStart, nowMinutes, onName, onCell}) => {
    const span = Math.max(range.end - range.start, 1);
    const percent = (minutes) => `${((Math.min(Math.max(minutes, range.start), range.end) - range.start) / span) * 100}%`;
    const width = (start, end) => `${((Math.min(end, range.end) - Math.max(start, range.start)) / span) * 100}%`;
    const ticks = hourTicks(range);

    return (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <div className="hidden lg:flex items-end gap-4 px-6 py-2">
                <div className="w-56 flex-shrink-0"/>
                <div className="relative flex-1 h-4">
                    {ticks.map((minutes) => (
                        <span key={minutes} style={{left: percent(minutes)}}
                              className="absolute -translate-x-1/2 text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
                            {clock(minutes)}
                        </span>
                    ))}
                </div>
                <div className="w-20 flex-shrink-0"/>
            </div>
            {rows.map((row) => {
                const cell = row.cells[0];
                const segments = cell.day ? workSegments(cell.day, dayStart, cell.status === "open" ? nowMinutes : null) : [];
                const partialRests = cell.status === "rest" || cell.status === "leave" ? [] : cell.rests;
                const off = STATUS_META[cell.status]?.hatch;
                return (
                    <div key={row.key} className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4 px-4 sm:px-6 py-3">
                        <div className="lg:w-56 flex-shrink-0 flex lg:flex-col items-center lg:items-start justify-between gap-2 min-w-0">
                            <button type="button" onClick={() => onName(row)} title={row.name}
                                    className={`truncate text-left text-sm font-semibold hover:underline ${row.unknown ? "text-amber-600" : "text-gray-900 dark:text-gray-100"}`}>
                                {row.name}
                            </button>
                            {STATUS_META[cell.status] && (
                                <button type="button" onClick={() => onCell(row, cell)} className="flex-shrink-0 hover:opacity-80">
                                    <StatusPill status={cell.status}/>
                                </button>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="relative h-7 rounded bg-gray-100 dark:bg-gray-700/50 overflow-hidden"
                                 style={off ? REST_HATCH : undefined}>
                                {ticks.map((minutes) => (
                                    <span key={minutes} style={{left: percent(minutes)}}
                                          className="absolute inset-y-0 w-px bg-white/70 dark:bg-gray-800/70"/>
                                ))}
                                {partialRests.map((rest) => {
                                    const start = Number(rest.start_time) * 60;
                                    const end = Number(rest.end_time) * 60;
                                    return (
                                        <span key={rest.id} title={`${restKindLabel(rest)} : ${restHoursLabel(rest)}`}
                                              style={{...REST_HATCH, left: percent(start), width: width(start, end)}}
                                              className="absolute inset-y-0 bg-gray-200/70 dark:bg-gray-600/50"/>
                                    );
                                })}
                                {segments.map((segment) => (
                                    <span key={segment.start}
                                          title={`${clock(segment.start)} – ${formatDuration(segment.end - segment.start)}`}
                                          style={{left: percent(segment.start), width: width(segment.start, segment.end)}}
                                          className={`absolute inset-y-1 rounded-sm ${segment.open ? "bg-green-400/80 animate-pulse" : "bg-primary"}`}/>
                                ))}
                                {cell.status === "missing_out" && (
                                    <span style={{left: percent(workDayMinutes(cell.day.punches.filter((p) => !p.duplicate).slice(-1)[0].time, dayStart))}}
                                          title="Entrée sans sortie"
                                          className="absolute inset-y-0 flex items-center -translate-x-1/2 text-amber-600">
                                        <AlertTriangle className="w-4 h-4"/>
                                    </span>
                                )}
                            </div>
                            {cell.day && (
                                <p className="mt-1 text-xs tabular-nums text-gray-500 dark:text-gray-400 truncate">{punchSummary(cell.day)}</p>
                            )}
                        </div>
                        <div className="lg:w-20 flex-shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                            {cell.day ? formatDuration(cell.day.worked_minutes) : ""}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── One employee: the period as a calendar ──────────────────────────────────

export const EmployeeCalendar = ({cells, today, selected, onSelect}) => {
    const leading = cells.length ? (new Date(`${cells[0].date}T12:00:00`).getDay() + 6) % 7 : 0;
    return (
        <div className="grid grid-cols-7 gap-1">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
                <div key={day} className="text-center text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400 pb-1">{day}</div>
            ))}
            {Array.from({length: leading}, (_, i) => <div key={`pad-${i}`}/>)}
            {cells.map((cell) => {
                const label = headerLabel(cell.date);
                const ring = cell.date === selected ? "ring-2 ring-primary" : cell.date === today ? "ring-1 ring-primary/50" : "";
                const content = (
                    <>
                        <span className="absolute top-1 left-1.5 text-[10px] font-medium opacity-70">{label.day}</span>
                        <span className="mt-2.5 text-xs">{cellText(cell)}</span>
                    </>
                );
                return STATUS_META[cell.status] ? (
                    <DayCell key={cell.date} cell={cell} label={label.long} onClick={() => onSelect(cell)} className={`h-14 ${ring}`}>
                        {content}
                    </DayCell>
                ) : (
                    <button key={cell.date} type="button" onClick={() => onSelect(cell)} title={label.long} aria-label={label.long}
                            className={`relative flex items-center justify-center h-14 rounded bg-gray-50 dark:bg-gray-800/60 text-gray-400 ${ring}`}>
                        {content}
                    </button>
                );
            })}
        </div>
    );
};
